import { LinearProgress } from '@mui/material';
import * as Sentry from '@sentry/react';
import type { LDContext } from 'launchdarkly-js-client-sdk';
import { LDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import type { LoaderFunctionArgs } from 'react-router';
import { useLoaderData, useLocation, useSearchParams } from 'react-router';

import {
  identifyUser,
  identifyUserId,
  markIdentityReady,
  setSuperProperties,
  useAnalytics,
  useEmailLinkTracking,
} from '~/analytics';
import { UserContext } from '~/auth/userContext';
import type { ItemType } from '~/components/common/SelectCheckbox';
import SideBarLayout from '~/components/common/SideBarLayout';
import { ContractEditDialogFlow } from '~/components/contractReview/ContractEditDialogFlow';
import { parseYearMonthDay } from '~/components/date/MonthRangePicker/MonthRangePicker';
import App from '~/components/global/App';
import { useCommandPalette } from '~/components/global/CommandPalette';
import { editingContractIdKey, editingContractSectionKey } from '~/components/global/constants';
import { DetailParamRedirects } from '~/components/global/DetailParamRedirects';
import { withGlobalFilters } from '~/components/global/globalFilterParams';
import { KnowledgeCaptureProvider } from '~/components/knowledge/KnowledgeCaptureProvider';
import { OttoIntegration, OttoSurface } from '~/components/otto/OttoIntegration';
import { UploadDocumentDialogFlow } from '~/components/uploadDocument/UploadDocumentDialogFlow';
import { WhatsNewCard } from '~/components/whatsNew/WhatsNewCard';
import { WhatsNewShowcase } from '~/components/whatsNew/WhatsNewShowcase';
import { ActionItemsRefreshProvider } from '~/contexts/actionItemsRefreshContext';
import { GlobalFilterContext } from '~/contexts/globalFilterContext';
import { clientsRetrieve } from '~/data/generated/endpoints/clients/clients';
import { consultantsRetrieve } from '~/data/generated/endpoints/consultants/consultants';
import { meRetrieve } from '~/data/generated/endpoints/me/me';
import { portfoliosList } from '~/data/generated/endpoints/portfolios/portfolios';
import { serviceProvidersList } from '~/data/generated/endpoints/service-providers/service-providers';
import type { API_UserGroupDetailResponse, User } from '~/data/schemas/user';
import { useUrlSyncedDateRange } from '~/hooks/useUrlSyncedDateRange';
import { getPortfoliosItems } from '~/lib/portfolio/getPortfoliosFromProperties';
import getRegionsFromPortfolio from '~/lib/portfolio/getRegionsFromPortfolio';
import { liftAiAuth } from '~/utils/api';
import { FlagsReadyContext, useFlagsReady } from '~/utils/featureFlags';
import { ensureDateRangeInParamsOrRedirect } from '~/utils/queryParams';

// Configure the react-pdf (pdf.js) worker once for the whole app. The AppShell is
// eagerly imported by the router and wraps every authenticated route, so this single
// assignment runs at boot and covers all PDF rendering (uploads, invoices, contracts,
// document review/split) without each route having to set the worker itself. A direct
// property assignment here (rather than a side-effect-only import module) survives
// production tree-shaking, since the module is retained for its component/loader exports.
pdfjs.GlobalWorkerOptions.workerSrc = '/js/pdfjs/pdf.worker.min.mjs';

const { VITE_GBP_CURRENCY_GROUP_NAME_REGEX: GBP_CURRENCY_GROUP_NAME_REGEX } = import.meta.env;

interface LoaderData {
  /** `id` is the Django user id: the Mixpanel, LaunchDarkly and Sentry identity. */
  user: { email?: string; id: string };
  currentUser: User;
  currencyCode: 'GBP' | 'USD';
  locale: 'en-GB' | 'en-US';
  userGroup: API_UserGroupDetailResponse | null;
  filterOptions: {
    portfolios: ItemType[];
    serviceProviders: ItemType[];
    serviceProvidersByPortfolio: Record<string, ItemType[]>;
    regions: ItemType[];
  };
  filters: {
    date: {
      start: string;
      end: string;
    };
  };
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { searchParams, pathname } = new URL(request.url);
  const { requireUser } = liftAiAuth();
  const { email, userId } = await requireUser(`${pathname}?${searchParams}`);

  // Pin the Mixpanel distinct_id to the authenticated user at the earliest
  // authenticated boundary, then release the `usePageTracking` identity gate.
  // On a post-login redirect / client navigation this loader resolves before
  // the route commits, so identify already wins. On a cold load (hard refresh
  // / bookmarked deep link) React Router commits the parent layout during this
  // loader's `hydrateFallbackElement` window, so identify alone loses the race
  // — `markIdentityReady` is what holds the first `page_viewed` until the user
  // is identified, keeping it off the anonymous `$device` id. Only
  // authenticated routes run this loader, so `/login`, `/reset-password` and
  // the `/auth/*` link pages stay anonymous. The richer people.set/register +
  // LaunchDarkly enrichment still runs in the AppContent effect.
  identifyUserId(userId);
  markIdentityReady();

  const [startDate, endDate] = ensureDateRangeInParamsOrRedirect(new URL(request.url));

  const [me, portfoliosFromApi, serviceProvidersFromApi] = await Promise.all([
    meRetrieve().then((r) => r.data),
    portfoliosList().then((r) => r.data),
    serviceProvidersList().then((r) => r.data),
  ]);

  // Normalize the generated UserDetail (role nullable, no 'none') to the app's
  // User shape. portfolios/providers stay on the legacy client until PR-2.
  const currentUser: User = { ...me, role: me.role ?? 'none' };

  let userGroup: API_UserGroupDetailResponse | null;
  if (currentUser.clientGroup) {
    userGroup = (await clientsRetrieve(currentUser.clientGroup)).data;
  } else if (currentUser.consultantGroup) {
    userGroup = (await consultantsRetrieve(currentUser.consultantGroup)).data;
  } else {
    userGroup = null;
  }

  const date = {
    start: startDate,
    end: endDate,
  };

  // Create the data for the portfolios filter
  const portfoliosToFilter = getPortfoliosItems(portfoliosFromApi);

  // Create the data for the service providers filter
  const serviceProvidersToFilter = serviceProvidersFromApi.map((provider) => ({
    id: provider.id,
    label: provider.name,
  }));

  // Build portfolio → service providers mapping
  const serviceProvidersByPortfolio: Record<string, ItemType[]> = {};
  for (const portfolio of portfoliosFromApi) {
    const providers: ItemType[] = [];
    const seen = new Set<string>();
    for (const property of portfolio.properties ?? []) {
      for (const sp of property.serviceProviders ?? []) {
        if (sp?.id && sp.name && !seen.has(sp.id)) {
          seen.add(sp.id);
          providers.push({ id: sp.id, label: sp.name });
        }
      }
    }
    serviceProvidersByPortfolio[portfolio.id] = providers;
  }

  // Get the regions from the portfolios
  const regionsFromPortfolios = getRegionsFromPortfolio(portfoliosFromApi);
  const regionsToFilter = regionsFromPortfolios.map((region) => ({
    id: region,
    label: region,
  }));

  const response: LoaderData = {
    user: { email, id: userId },
    currentUser: {
      ...currentUser,
      role: currentUser.role ?? 'none',
    },
    userGroup: userGroup,
    currencyCode: GBP_CURRENCY_GROUP_NAME_REGEX
      ? userGroup?.name.match(new RegExp(GBP_CURRENCY_GROUP_NAME_REGEX))
        ? 'GBP'
        : 'USD'
      : 'USD',
    locale: GBP_CURRENCY_GROUP_NAME_REGEX
      ? userGroup?.name.match(new RegExp(GBP_CURRENCY_GROUP_NAME_REGEX))
        ? 'en-GB'
        : 'en-US'
      : 'en-US',
    filterOptions: {
      portfolios: portfoliosToFilter,
      serviceProviders: serviceProvidersToFilter,
      serviceProvidersByPortfolio,
      regions: regionsToFilter,
    },
    filters: {
      date,
    },
  };

  return response;
};

/**
 * True when `route` is the current URL's top-level section — the section itself or
 * anything nested under it, so `/spending/:invoiceId` still marks Spending.
 *
 * Matched against the URL rather than by index into `useMatches()`: the pathless
 * `RootLayout` is itself a match, so `matches[1]` is the AppShell at `/` and no nav
 * item points there. `/` is exact-only — every path would otherwise be under it.
 */
export const isTopLevelRouteMatch = (currentPathname: string, route: string): boolean =>
  currentPathname === route || (route !== '/' && currentPathname.startsWith(`${route}/`));

const useTopLevelRouteMatch = () => {
  const { pathname } = useLocation();

  return (route: string) => isTopLevelRouteMatch(pathname, route);
};

/**
 * Seconds the SDK waits for its first flag payload before publishing a client with no
 * flags. A returning user resolves from the `localStorage` cache on the next tick
 * whatever this is, so it only bounds a first visit or a stalled connection. Flags that
 * arrive after it still reach the app.
 */
const LAUNCHDARKLY_TIMEOUT_SECONDS = 2;

/**
 * When the shell stops waiting even though the provider never published a client. The
 * SDK timeout covers a slow or failed request; this covers what it does not, such as
 * `initialize` throwing inside the provider's `componentDidMount`, where nothing
 * catches it and the client would never appear.
 */
const FLAGS_READY_DEADLINE_MS = (LAUNCHDARKLY_TIMEOUT_SECONDS + 1) * 1000;

/**
 * Publishes {@link FlagsReadyContext} from inside `LDProvider`, and handles the parts of
 * the client's lifecycle the provider leaves out.
 */
const LaunchDarklyReadiness = ({
  children,
  context,
}: {
  children: React.ReactNode;
  context: LDContext;
}) => {
  // `LDProvider` publishes its client once `waitForInitialization` settles, whether it
  // resolved, failed or timed out, so its arrival is the ready signal.
  const ldClient = useLDClient();
  const [pastDeadline, setPastDeadline] = useState(false);

  useEffect(() => {
    if (ldClient) return;
    const timer = setTimeout(() => setPastDeadline(true), FLAGS_READY_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [ldClient]);

  // `LDProvider` never closes the client it creates, and its `change` listener keeps a
  // stream open. Logging out unmounts the shell without a page load, so without this
  // every login in the tab left a client streaming for the previous user.
  useEffect(() => {
    if (!ldClient) return;
    return () => {
      void ldClient.close();
    };
  }, [ldClient]);

  // `LDProvider` reads `context` once, at mount. When the loader revalidates with a new
  // email or role, `identify` swaps the flags in place; remounting the provider would
  // rebuild the shell.
  const contextKey = JSON.stringify(context);
  const identifiedContextKey = useRef(contextKey);
  useEffect(() => {
    if (!ldClient || identifiedContextKey.current === contextKey) return;
    identifiedContextKey.current = contextKey;
    ldClient.identify(context).catch((error: unknown) => {
      console.error('Failed to identify LaunchDarkly context:', error);
    });
  }, [ldClient, context, contextKey]);

  return (
    <FlagsReadyContext.Provider value={ldClient !== undefined || pastDeadline}>
      {children}
    </FlagsReadyContext.Provider>
  );
};

/**
 * Renders the same element types from the first render to the last. It used to render
 * `<>{children}</>` until `asyncWithLDProvider` resolved and the provider it returned
 * after, and that change of type above the shell made React unmount and rebuild
 * everything beneath it once per cold load: a page's mount effects ran twice, so
 * `invoice_opened` fired twice against one `page_viewed`.
 */
const LaunchDarklyProvider = ({
  children,
  user,
  currentUser,
}: {
  children: React.ReactNode;
  user: LoaderData['user'];
  currentUser: User;
}) => {
  const clientSideID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;

  // Same shape and key order as the loader's client in `~/utils/launchDarkly`: the
  // bootstrap cache is keyed by the serialized context, so the two share it.
  const context = useMemo<LDContext>(
    () => ({ kind: 'user', key: user.id, email: user.email, role: currentUser.role }),
    [user.id, user.email, currentUser.role],
  );

  useEffect(() => {
    if (!clientSideID) console.warn('LaunchDarkly client ID not found');
  }, [clientSideID]);

  // Screenshot mode sets no client ID. Nothing will be delivered, every flag reads its
  // default and `FlagsReadyContext` defaults to ready. The ID is fixed for the build, so
  // this branch never trades places with the one below.
  if (!clientSideID) {
    return <>{children}</>;
  }

  return (
    <LDProvider
      clientSideID={clientSideID}
      context={context}
      // A returning user's flags come from the cache before the network answers, so a
      // rollout does not show them the flag-off shell first.
      options={{ bootstrap: 'localStorage' }}
      timeout={LAUNCHDARKLY_TIMEOUT_SECONDS}
    >
      <LaunchDarklyReadiness context={context}>{children}</LaunchDarklyReadiness>
    </LDProvider>
  );
};

const AppContent = ({
  user,
  currentUser,
  userGroup,
  currencyCode,
  locale,
  filterOptions: unparsedFilterOptions,
  filters: unparsedFilters,
}: LoaderData) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const routeMatches = useTopLevelRouteMatch();
  const hasIdentified = useRef(false);
  const { track } = useAnalytics();

  const [globalFilterContextPartial, setGlobalFilterContext] = useState<
    Omit<GlobalFilterContext, 'onGlobalFiltersChange'>
  >({
    options: {
      portfolios: unparsedFilterOptions.portfolios,
      serviceProviders: unparsedFilterOptions.serviceProviders,
      serviceProvidersByPortfolio: unparsedFilterOptions.serviceProvidersByPortfolio,
      regions: unparsedFilterOptions.regions,
    },
    selectedFilters: {
      portfolios: [],
      serviceProviders: [],
      regions: [],
      date: {
        start: parseYearMonthDay(unparsedFilters.date.start),
        end: parseYearMonthDay(unparsedFilters.date.end),
      },
    },
  });

  // The selection above is seeded once, from the loader, but the URL's range is
  // rewritten by things the filter bar never touches — the app shell's own
  // default-range redirect most of all. Without this the Period chip, the URL and the
  // range sent to the API drifted apart, and the URL was the wrong one.
  useUrlSyncedDateRange(
    globalFilterContextPartial.selectedFilters.date,
    useCallback((date) => {
      setGlobalFilterContext((prev) => ({
        ...prev,
        selectedFilters: { ...prev.selectedFilters, date },
      }));
    }, []),
  );

  const globalFilterContext: GlobalFilterContext = useMemo(
    () => ({
      ...globalFilterContextPartial,
      onGlobalFiltersChange: (filters, options) => {
        // A silent change is the app reconciling a selection, not the user
        // applying one; counting it would inflate filter engagement.
        if (!options?.silent) {
          track('global_filter_applied', {
            portfolio_count: filters.portfolios.length,
            service_provider_count: filters.serviceProviders.length,
            region_count: filters.regions.length,
          });
        }
        setGlobalFilterContext((prev) => ({
          ...prev,
          selectedFilters: filters,
        }));
        setSearchParams((searchParams) => withGlobalFilters(searchParams, filters));
      },
    }),
    [globalFilterContextPartial, setSearchParams, track],
  );

  useEffect(() => {
    if (hasIdentified.current) return;
    // The loader resolves currentUser/userGroup synchronously today,
    // so this is mostly defensive — but pinning the guard to "we
    // actually have a profile to identify with" protects against a
    // future refactor that loads any of these lazily and would
    // otherwise capture a partial profile permanently.
    if (!user || !currentUser.id) return;
    hasIdentified.current = true;
    Sentry.setUser({ email: user.email, id: user.id });
    window.dataLayer?.push({
      event: 'identify',
      userId: user.id,
    });
    // First-page-view attribution is handled earlier, in the app-shell
    // loader (`identifyUserId(userId)`), which resolves before the router
    // commits `RootLayout` and its `usePageTracking`. This effect only
    // enriches the already-identified profile with people.set/register
    // data that isn't available in the loader (full profile, group). We do
    // NOT rely on child-before-parent effect ordering to beat `page_viewed`
    // anymore — that assumption didn't hold across the loader/lazy boundary
    // and mis-attributed the first authenticated page view to the anonymous
    // `$device` id.
    identifyUser({
      userId: user.id,
      email: user.email,
      role: currentUser.role,
      isLiftaiAdmin: currentUser.isLiftaiAdmin,
      customerGroupId: userGroup?.id ?? null,
      customerGroupName: userGroup?.name ?? null,
      customerGroupKind: currentUser.clientGroup
        ? 'client'
        : currentUser.consultantGroup
          ? 'consultant'
          : null,
      currencyCode,
      locale,
    });
  }, [user, currentUser, userGroup, currencyCode, locale]);

  // LaunchDarkly flag enrichment — push the current variation of every
  // flag as a Mixpanel super-property so events arrive pre-segmented by
  // cohort. `useFlags()` returns a fresh object reference on every LD
  // streaming update, so we cache the last-sent JSON serialization in
  // a ref and skip the register call when contents haven't changed.
  const flags = useFlags();
  const flagsReady = useFlagsReady();
  const lastFlagsJsonRef = useRef<string>('');
  useEffect(() => {
    if (!flags || Object.keys(flags).length === 0) return;
    const sortedEntries = Object.entries(flags).sort(([a], [b]) => a.localeCompare(b));
    const serialized = JSON.stringify(sortedEntries);
    if (serialized === lastFlagsJsonRef.current) return;
    lastFlagsJsonRef.current = serialized;
    const flagProps: Record<string, unknown> = {};
    for (const [key, value] of sortedEntries) {
      flagProps[`ld_${key}`] = value;
    }
    setSuperProperties(flagProps);
  }, [flags]);

  // `usePageTracking` was moved to the router's RootLayout so it covers
  // both authenticated and unauthenticated routes. Calling it here would
  // double-fire `page_viewed` for every authed-shell mount.

  // Weekly invoice summary email click-through attribution (fires
  // `weekly_invoice_email_visited` and strips the utm_* params on landing).
  useEmailLinkTracking();

  const { openCommandPalette, element: commandPaletteElement } = useCommandPalette({
    role: currentUser.role,
  });

  const onContractEdit = useCallback(
    (contractId: string | null) => {
      setSearchParams((searchParams) => {
        if (contractId === null) {
          searchParams.delete(editingContractIdKey);
          // The section request belongs to the form that just closed; leaving it
          // behind would reopen the next contract on the rules tab.
          searchParams.delete(editingContractSectionKey);
        } else {
          searchParams.set(editingContractIdKey, contractId);
        }
        return searchParams;
      });
    },
    [setSearchParams],
  );

  return (
    <UserContext.Provider
      value={{
        user,
        userDetails: currentUser,
        userGroup,
        currencyCode,
        locale,
      }}
    >
      <GlobalFilterContext.Provider value={globalFilterContext}>
        <ActionItemsRefreshProvider>
          {/*
          Nothing below mounts until LaunchDarkly is ready, so every component in it
          first renders with flags delivered. Rendering it early does not only
          flicker: OttoIntegration and OttoSurface change element type when `aiChat`
          arrives, which would rebuild the whole subtree for anyone with Otto on.
          The bar matches the router's `hydrateFallbackElement`, so a cold load
          shows one continuous loading state.

          OttoIntegration mounts the OttoProvider, so it MUST wrap the dialogs below
          it. The invoice reject dialog reads Otto's context via useOttoOptional() to
          offer the "Generate review rules" CTA; nested the other way round that
          dialog falls outside the provider, useOttoOptional() returns null, and the
          CTA silently never renders. OttoChatRunners is headless (returns null) and
          only consumes the Otto context, so hoisting it here is safe.
        */}
          {flagsReady ? (
            <OttoIntegration>
              <DetailParamRedirects />
              <ContractEditDialogFlow
                editingContractId={searchParams.get(editingContractIdKey)}
                onContractEdit={onContractEdit}
              />
              <UploadDocumentDialogFlow>
                <KnowledgeCaptureProvider>
                  <SideBarLayout routeMatches={routeMatches} openSearch={openCommandPalette}>
                    <OttoSurface>
                      <App />
                    </OttoSurface>
                  </SideBarLayout>
                  {commandPaletteElement}
                  <WhatsNewCard />
                  <WhatsNewShowcase />
                </KnowledgeCaptureProvider>
              </UploadDocumentDialogFlow>
            </OttoIntegration>
          ) : (
            <LinearProgress color="info" />
          )}
        </ActionItemsRefreshProvider>
      </GlobalFilterContext.Provider>
    </UserContext.Provider>
  );
};

export function AppShell() {
  const loaderData = useLoaderData<typeof loader>();

  return (
    <LaunchDarklyProvider user={loaderData.user} currentUser={loaderData.currentUser}>
      <AppContent {...loaderData} />
    </LaunchDarklyProvider>
  );
}
